/**
 * Standard Automotive Spare Parts Taxonomy & Master Catalog Data
 * Based on authentic OEM & Aftermarket automotive classifications.
 */

export interface PartCategoryDefinition {
  id: string
  name: string
  iconName?: string
  description?: string
  subParts: string[]
}

export const PARTS_CATEGORIES_DATA: PartCategoryDefinition[] = [
  {
    id: 'engine-mechanical',
    name: 'Engine & Mechanical Parts',
    description: 'Engine assembly, pistons, cylinder head, crankshaft, mounts, timing components',
    subParts: [
      'Engine Assembly',
      'Pistons & Rings',
      'Cylinder Head',
      'Crankshaft & Camshaft',
      'Engine Mounts',
      'Timing Belt & Chain Kit',
      'Valves & Lifters',
      'Gaskets & Seals',
    ],
  },
  {
    id: 'transmission-clutch',
    name: 'Transmission & Clutch',
    description: 'Gearbox, clutch plate, clutch assembly, flywheel, driveshaft',
    subParts: [
      'Manual Gearbox',
      'Automatic Transmission',
      'Clutch Plate',
      'Clutch Assembly Kit',
      'Flywheel & Dual Mass Flywheel',
      'Driveshaft & Propeller Shaft',
      'Differential Gearbox',
    ],
  },
  {
    id: 'brakes',
    name: 'Brakes',
    description: 'Brake pads, discs, calipers, master cylinder, ABS components',
    subParts: [
      'Brake Pads (Front / Rear)',
      'Brake Discs & Rotors',
      'Brake Calipers',
      'Master Cylinder',
      'ABS Sensors & Modulator',
      'Brake Lines & Hoses',
      'Handbrake Cable & Actuator',
    ],
  },
  {
    id: 'suspension-steering',
    name: 'Suspension & Steering',
    description: 'Shock absorbers, struts, control arms, ball joints, steering rack',
    subParts: [
      'Shock Absorbers (Front / Rear)',
      'Struts & Coil Springs',
      'Lower / Upper Control Arms',
      'Ball Joints & Bushings',
      'Steering Rack & Pinion',
      'Tie Rod Ends',
      'Stabilizer / Sway Bar Links',
    ],
  },
  {
    id: 'electrical-electronics',
    name: 'Electrical & Electronics',
    description: 'Alternator, starter motor, ECU, sensors, wiring harness, switches',
    subParts: [
      'Alternator',
      'Starter Motor',
      'Engine Control Unit (ECU)',
      'O2 / Oxygen Sensor',
      'Crank / Cam Sensor',
      'Wiring Harness Assembly',
      'Combination & Power Window Switches',
      'Fuse Box & Relays',
    ],
  },
  {
    id: 'cooling-hvac',
    name: 'Cooling & HVAC',
    description: 'Radiator, cooling fan, water pump, condenser, compressor, blower motor',
    subParts: [
      'Radiator Assembly',
      'Electric Cooling Fan',
      'Engine Water Pump',
      'AC Condenser',
      'AC Compressor',
      'Blower Motor & Resistor',
      'Thermostat Valve & Housing',
      'Coolant Expansion Tank',
    ],
  },
  {
    id: 'body-exterior',
    name: 'Body & Exterior',
    description: 'Bumper, bonnet, fender, doors, grille, mirrors, headlights, taillights',
    subParts: [
      'Front / Rear Bumper',
      'Bonnet / Hood',
      'Fender / Quarter Panel',
      'Doors & Tailgate',
      'Front Grille',
      'Side View Mirrors',
      'Headlights (LED / Halogen)',
      'Taillights & Indicators',
    ],
  },
  {
    id: 'interior-parts',
    name: 'Interior Parts',
    description: 'Seats, dashboard, door trims, window switches, infotainment, interior handles',
    subParts: [
      'Seats & Upholstery',
      'Dashboard Assembly',
      'Door Trims & Panels',
      'Power Window Switches',
      'Infotainment & Touchscreen Head Unit',
      'Interior Door Handles',
      'Steering Wheel & Controls',
      'Center Console & Armrest',
    ],
  },
  {
    id: 'fuel-exhaust',
    name: 'Fuel & Exhaust',
    description: 'Fuel pump, injectors, throttle body, exhaust manifold, catalytic converter',
    subParts: [
      'High-Pressure Fuel Pump',
      'Fuel Injectors',
      'Throttle Body',
      'Exhaust Manifold',
      'Catalytic Converter',
      'Exhaust Muffler & End Pipes',
      'Fuel Tank & Filter Assembly',
    ],
  },
  {
    id: 'wheels-tyres',
    name: 'Wheels & Tyres',
    description: 'Alloy wheels, steel rims, wheel hubs, tyres',
    subParts: [
      'Alloy Wheels',
      'Steel Rims',
      'Wheel Hub & Bearings',
      'Performance / All-Season Tyres',
      'Tyre Pressure Sensors (TPMS)',
      'Lug Nuts & Spacers',
    ],
  },
  {
    id: 'filters-service-parts',
    name: 'Filters & Service Parts',
    description: 'Oil filter, air filter, cabin filter, belts, wiper blades',
    subParts: [
      'Engine Oil Filter',
      'Engine Air Filter',
      'AC Cabin Pollen Filter',
      'Fuel Filter',
      'Drive & Serpentine Belts',
      'Wiper Blades (Pair)',
      'Spark Plugs / Glow Plugs',
    ],
  },
]

export const PART_CONDITIONS = [
  { value: 'ALL', label: 'All Conditions' },
  { value: 'NEW', label: 'New / Factory OEM' },
  { value: 'USED', label: 'Used / Tested' },
  { value: 'REFURBISHED', label: 'Refurbished' },
] as const

export const VEHICLE_TYPES = [
  { value: 'CAR', label: 'Cars' },
  { value: 'BIKE', label: 'Bikes & 2-Wheelers' },
  { value: 'COMMERCIAL', label: 'Commercial Vehicles' },
] as const

export const AVAILABILITY_STATUSES = [
  { value: 'ALL', label: 'All Availability' },
  { value: 'IN_STOCK', label: 'In Stock' },
  { value: 'ENQUIRY_REQUIRED', label: 'Enquiry Required' },
] as const

export interface VehicleBrandModels {
  brand: string
  models: string[]
}

export const POPULAR_VEHICLE_BRANDS: VehicleBrandModels[] = [
  {
    brand: 'Maruti Suzuki',
    models: ['Swift', 'Baleno', 'Brezza', 'Dzire', 'Ertiga', 'WagonR', 'Grand Vitara', 'Fronx', 'Jimny'],
  },
  {
    brand: 'Hyundai',
    models: ['Creta', 'i20', 'Verna', 'Venue', 'Tucson', 'Aura', 'Exter', 'Alcazar'],
  },
  {
    brand: 'Tata',
    models: ['Nexon', 'Harrier', 'Safari', 'Punch', 'Altroz', 'Tiago', 'Tigor', 'Curvv'],
  },
  {
    brand: 'Mahindra',
    models: ['Thar', 'Scorpio-N', 'Scorpio Classic', 'XUV700', 'XUV300 / XUV 3XO', 'Bolero'],
  },
  {
    brand: 'Toyota',
    models: ['Fortuner', 'Innova Crysta', 'Innova Hycross', 'Urban Cruiser Hyryder', 'Glanza', 'Hilux', 'Camry'],
  },
  {
    brand: 'Honda',
    models: ['City', 'Amaze', 'Elevate', 'Civic', 'CR-V'],
  },
  {
    brand: 'Volkswagen',
    models: ['Virtus', 'Taigun', 'Polo', 'Vento', 'Tiguan'],
  },
  {
    brand: 'Skoda',
    models: ['Slavia', 'Kushaq', 'Octavia', 'Superb', 'Kodiaq'],
  },
  {
    brand: 'Kia',
    models: ['Seltos', 'Sonet', 'Carens', 'EV6', 'Carnival'],
  },
  {
    brand: 'BMW',
    models: ['3 Series', '5 Series', '7 Series', 'X1', 'X3', 'X5', 'X7', 'M3', 'M4', 'M5'],
  },
  {
    brand: 'Mercedes-Benz',
    models: ['C-Class', 'E-Class', 'S-Class', 'GLA', 'GLC', 'GLE', 'GLS', 'G-Wagon', 'AMG GT'],
  },
  {
    brand: 'Audi',
    models: ['A4', 'A6', 'A8', 'Q3', 'Q5', 'Q7', 'Q8', 'RS5', 'e-tron'],
  },
  {
    brand: 'Porsche',
    models: ['911', '718 Cayman', 'Panamera', 'Cayenne', 'Macan', 'Taycan'],
  },
]

export const POPULAR_YEARS = Array.from({ length: 25 }, (_, i) => 2026 - i)

export const ENGINE_VARIANTS = [
  '1.0L Turbo Petrol',
  '1.2L Naturally Aspirated Petrol',
  '1.4L Turbo Petrol',
  '1.5L Petrol',
  '1.5L Turbo Diesel',
  '2.0L Turbo Petrol',
  '2.0L Turbo Diesel',
  '3.0L V6 Twin-Turbo',
  'Electric (EV Motor)',
  'Hybrid (Petrol + Electric)',
]
